<template>
    <div class="sub-facility-wrapper">
        <div class="sub-facility-title ">
            {{ hierarchy }}
            <span class="badge title"> {{ facilityIdx }} </span>
            <span v-if="newCT" class="badge id"> CT : {{ newCT }} </span>
        </div>

        <!-- 탭 네비게이션 -->
        <ul class="custom-tabs">
            <li
                v-for="(tab, index) in tabs"
                :key="index"
                class="custom-tab"
                :class="{ active: activeTab === index }"
                @click="handleTabClick(index)"
            >
                <span class="mr-1"><app-icon :icon="tab.icon" /></span>
                <span>{{ tab.label }}</span>
            </li>
        </ul>

        <!-- 탭 컨텐츠 -->
        <div class="sub-facility-tab-content">
            <!-- 첫 번째 탭: 수집 항목 설정 -->
            <div v-if="activeTab === 0" class="sub-facility-tab-pane">
                <PointMapping
                    :facilityIdx="facilityIdx"
                    :facility="facility"
                    :hierarchy="hierarchy"
                    :parentFacilityName="parentFacilityName"
                />
            </div>

            <!-- 두 번째 탭: 서브모듈 설정 -->
            <div v-else-if="activeTab === 1" class="sub-facility-tab-pane sub-facility-content-container">
                <div class="sub-facility-content">
                    <FacilityPopupBody
                        :newFacility="newFacility"
                        :parentFacility="parentFacility"
                        :facilityIdx="facilityIdx"
                        :parentFacilityIdx="parentFacilityIdx"
                        :drivers="drivers"
                        :driverTypes="driverTypes"
                        :getVersion="getVersion"
                        :rules="rules"
                        :validMessage="validMessage"
                        :classes="classes"
                        :isUpdate="isUpdate"
                        :meterTypeCode="meterTypeCode"
                        :measTypeCode="measTypeCode"
                        :childrens="childrens"
                        :newCT="newCT"
                        :aliasSynced="aliasSynced"
                        :mode="mode"
                        @input-facility-alias="onInputFacilityAlias"
                        @set-pulse-unit="onSetPulseUnit"
                        class=""
                    />

                    <!-- 계측기 정보 추가 끝 -->
                    <div class="w-100 mt-3">
                        <!-- GS 테스트를 위한 임시 작업, 20250617  -->
                        <!-- v-xe-pm.E 삭제 -->
                        <button class="btn btn-danger" v-if="isUpdate" @click="onDeleteFacility()">
                            <i class="fa fa-times"></i> {{ $t("삭제") }}
                        </button>
                        <button class="btn btn-primary pull-right" @click="onSaveFacility()">
                            <i class="fa fa-save"></i> {{ $t("저장") }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import PointMapping from "./PointMapping.vue";
import FacilityPopupBody from "./FacilityPopupBody.vue";
import backEndApi from "@api/backEndApi.js";
import * as rules from "@src/consts/ruleConsts.js";
import * as validMessage from "@src/consts/validMessageConsts";
import * as popupMessages from "@src/consts/popupMessageConsts";
import { mapGetters } from "vuex";

export default {
    components: {
        PointMapping,
        FacilityPopupBody,
    },
    props: {
        facilityIdx: {
            type: [String, Number],
            required: true,
        },
        facility: {
            type: Object,
            required: true,
        },
        hierarchy: {
            type: String,
            default: "",
        },
        channelType: {
            type: String,
            default: "",
        },
        parentFacilityIdx: {
            type: [String, Number],
            default: null,
        },
        parentFacilityName: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            /* 탭 정의 */
            tabs: [
                {
                    label: "수집 항목 설정",
                    icon: "fa fa-link",
                },
                {
                    label: "서브모듈 설정",
                    icon: "fa fa-info-circle",
                },
            ],
            activeTab: 0,

            // FacilityPopupBody에 필요한 데이터들
            classes: "",
            parentFacility: {},
            newFacility: {},
            orgFacility: {},
            aliasSynced: true,
            drivers: [],
            rules,
            validMessage,
            popupMessages,
            facilityOption: {
                pulseUnit: null,
            },
        };
    },
    computed: {
        ...mapGetters(["commonCodes", "zoneInfo"]),

        isUpdate() {
            return !this.isEmpty(this.facilityIdx);
        },

        driverTypes() {
            // version이 여러개면, driverType이 중복되므로 distinct 해준다.
            if (!this.drivers || this.drivers.length === 0) {
                return [];
            }
            const types = [...new Set(this.drivers.map((v) => v.driverType))];
            return types;
        },

        getVersion() {
            if (!this.drivers || this.drivers.length === 0 || !this.newFacility.driverType) {
                return [];
            }
            const versions = this.drivers.filter((driver) => driver.driverType === this.newFacility.driverType);
            return versions;
        },

        meterTypeCode() {
            try {
                const result = this.commonCodes.meterTypeCode.find(
                    (item) => item.value == this.newFacility.meterTypeCode
                );
                return result.text ?? "-";
            } catch (err) {
                return "-";
            }
        },

        measTypeCode() {
            try {
                const result = this.commonCodes.measTypeCode.find(
                    (item) => item.value == this.newFacility.measTypeCode
                );
                return result.text ?? "-";
            } catch (err) {
                return "-";
            }
        },

        childrens() {
            return this.facility.childrens || [];
        },

        newCT() {
            return this.facility.ct || null;
        },

        mode() {
            return "panel";
        },
    },

    watch: {
        facilityIdx: {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.initSetting();
                }
            },
            immediate: true,
        },

        "newFacility.facilityName": function(newVal) {
            if (this.aliasSynced) this.newFacility.facilityAlias = newVal;
        },
    },

    mounted() {
        this.initSetting();
    },

    methods: {
        // ===== CRUD 작업 관련 메서드 =====
        async onDeleteFacility() {
            try {
                const result = await this.alertConfirmWarning(popupMessages.COMMON_DELETE_CONFIRM_POPUP_MESSAGE);
                if (!result.value) return;

                const { data, status } = await backEndApi.facility.deleteFacility(this.newFacility.facilityIdx);
                if (this.checkError(data, status)) {
                    console.error("삭제 장비 API 응답 에러:", data, status);
                    throw new Error(data.message);
                }

                this.newFacility = {};
                this.alertNoti(popupMessages.COMMON_DELETE_POPUP_MESSAGE);
                this.$emit("save-done");
            } catch (error) {
                console.error("삭제 장비 네트워크 에러:", error);
                this.alertWarning(error);
            }
        },

        onSaveFacility() {
            if (!this.validateFacilityData()) return;
            this.updateFacility();
        },

        async updateFacility() {
            if (!this.validateFacilityUpdate()) return;

            const result = await this.alertQuestion(popupMessages.COMMON_UPDATE_CONFIRM_POPUP_MESSAGE);
            if (!result.value) return;

            await this.executeFacilityUpdate();
        },

        async executeFacilityUpdate() {
            try {
                this.prepareFacilityOption();
                await this.updateFacilityData();
                this.handleUpdateSuccess();
            } catch (error) {
                this.alertWarning(error);
            }
        },

        async updateFacilityData() {
            try {
                const { status, data } = await backEndApi.facility.updateFacility(this.newFacility);

                if (status !== 200) {
                    throw new Error(data.message);
                }

                this.alertNoti(popupMessages.COMMON_UPDATE_POPUP_MESSAGE);
                return { success: true };
            } catch (error) {
                this.alertWarning(error);
                return { success: false };
            }
        },

        handleUpdateSuccess() {
            this.alertNoti(popupMessages.COMMON_UPDATE_POPUP_MESSAGE);
            this.initSetting();
        },

        // ===== 유효성 검사 관련 메서드 =====
        validateFacilityData() {
            if (this.isEmpty(this.newFacility.driverType) || this.isEmpty(this.newFacility.version)) {
                this.alertNoti(popupMessages.POINT_DATA_COLLECTION_ADD_DRIVER_VALID_POPUP_MESSAGE);
                return false;
            }

            if (this.newCT === undefined) {
                if (!this.validateFacilityName()) return false;
                if (!this.validateFacilityAlias()) return false;
            }

            return true;
        },

        validateFacilityName() {
            const regex = /^[A-Za-z0-9\-._]+$/;
            if (!regex.test(this.newFacility.facilityName?.toUpperCase())) {
                this.alertNoti(popupMessages.POINT_DATA_COLLECTION_ADD_ID_VALID_POPUP_MESSAGE);
                return false;
            }
            return true;
        },

        validateFacilityAlias() {
            if (this.isEmpty(this.newFacility.facilityAlias)) {
                this.alertNoti(popupMessages.POINT_DATA_COLLECTION_ADD_NAME_VALID_POPUP_MESSAGE);
                return false;
            }
            return true;
        },

        validateFacilityUpdate() {
            if (this.hasDriverChanged()) {
                this.alertNoti(popupMessages.POINT_DATA_COLLECTION_DRIVER_UPDATE_ERROR_POPUP_MESSAGE);
                return false;
            }

            if (this.hasFacilityNameChanged()) {
                this.alertNoti(popupMessages.POINT_DATA_COLLECTION_PTADDR_CHANGE_POPUP_MESSAGE);
            }

            return true;
        },

        // ===== 데이터 초기화 관련 메서드 =====
        initSetting() {
            this.initializeNewFacility();
            this.loadDrivers();
            this.setupCT();
        },

        initializeNewFacility() {
            this.newFacility = {
                ...this.facility,
                serverIdx: this.facility.serverIdx || null,
                channelIdx: this.facility.channelIdx || null,
                parentFacilityIdx: this.parentFacilityIdx,
                facilityOption: this.facility.facilityOption || {},
                driverType: this.facility.driverType || "",
                deviceId: this.facility.deviceId || "",
                mappingKey: this.facility.mappingKey || "",
                facilityName: this.facility.facilityName || "",
                applyZoneIdx: this.facility.applyZoneIdx || null,
                energySourceCode: this.facility.energySourceCode || null,
                energySplyTypeCode: this.facility.energySplyTypeCode || null,
                isUse: this.facility.isUse !== undefined ? this.facility.isUse : true,
                instDate: this.facility.instDate || new Date().toISOString().slice(0, 10),
                clbrPeriodCode: this.facility.clbrPeriodCode || "A006",
                purpsUseCode: this.facility.purpsUseCode || null,
                ct: this.facility.ct || null,
            };

            this.setupPulseUnit();
        },

        setupPulseUnit() {
            if (this.parentFacility && this.parentFacility.measTypeCode == "Pulse") {
                this.newFacility.facilityOption.pulseUnit = null;
            }
        },

        async loadDrivers() {
            try {
                const { data, status } = await backEndApi.driver.searchDriver();

                // HTTP 상태 체크
                if (this.checkHttpStatus(status)) {
                    console.error("드라이버 API HTTP 에러:", status);
                    return;
                }

                // API 응답 데이터 체크
                if (this.checkApiResponse(data)) {
                    console.error("드라이버 API 응답 에러:", data);
                    return;
                }

                if (this.isEmpty(this.channelType)) return;

                this.setupDrivers(data);
                await this.loadFacilityDetails();
            } catch (error) {
                // 네트워크 에러, Promise rejection 등
                console.error("드라이버 로딩 네트워크 에러:", error);
            }
        },

        setupDrivers(driverData) {
            const filteredDrivers = driverData.filter((v) => v.channelType == this.channelType);

            this.drivers = [
                {
                    channelType: "",
                    driverName: "",
                    driverType: "",
                    remark: "",
                    version: "",
                },
                ...filteredDrivers,
            ];

            if (this.drivers.length <= 1) {
                this.alertNoti(popupMessages.POINT_DATA_COLLECTION_INIT_ERROR_POPUP_MESSAGE);
                return;
            }
        },

        async loadFacilityDetails() {
            if (!this.isUpdate) {
                this.bindParentDriver();
                return;
            }

            try {
                const { data, status } = await backEndApi.facility.getFacility(this.facilityIdx);

                // HTTP 상태 체크
                if (this.checkHttpStatus(status)) {
                    console.error("장비 상세 API HTTP 에러:", status);
                    return;
                }

                // API 응답 데이터 체크
                if (this.checkApiResponse(data)) {
                    console.error("장비 상세 API 응답 에러:", data);
                    return;
                }

                this.processFacilityData(data);
            } catch (error) {
                // 네트워크 에러, Promise rejection 등
                console.error("장비 상세 정보 로딩 네트워크 에러:", error);
            }
        },

        processFacilityData(data) {
            this.newFacility = data;
            this.parseFacilityOption(data);
            this.setupFacilityProperties(data);
            this.orgFacility = Object.freeze(data);
            this.aliasSynced = this.newFacility.facilityName == this.newFacility.facilityAlias;
            this.bindParentDriver();
        },

        parseFacilityOption(data) {
            try {
                if (typeof data.facilityOption === "string") {
                    this.newFacility.facilityOption = JSON.parse(data.facilityOption);
                }
            } catch (err) {
                console.error("Invalid Json Format", err);
            }
        },

        setupFacilityProperties(data) {
            this.newFacility.applyZoneIdx = data.applyZoneIdx;
            this.newFacility.energySourceCode = data.energySourceCode;
            this.newFacility.energySplyTypeCode = data.energySplyTypeCode;
        },

        setupCT() {
            if (this.newCT) {
                this.newFacility.ct = this.newCT;
            }
        },

        async bindParentDriver() {
            if (!this.parentFacilityIdx) {
                this.setDefaultParentFacility();
                return;
            }

            try {
                const { data, status } = await backEndApi.facility.getFacility(this.parentFacilityIdx);

                // HTTP 상태 체크
                if (this.checkHttpStatus(status)) {
                    console.error("부모 장비 API HTTP 에러:", status);
                    return;
                }

                // API 응답 데이터 체크
                if (this.checkApiResponse(data)) {
                    console.error("부모 장비 API 응답 에러:", data);
                    return;
                }

                this.parentFacility = data;
                this.syncParentDriverInfo(data);
            } catch (error) {
                // 네트워크 에러, Promise rejection 등
                console.error("부모 장비 정보 로딩 네트워크 에러:", error);
            }
        },

        setDefaultParentFacility() {
            this.parentFacility = {
                facilityName: "부모 시설",
                driverType: "",
                measTypeCode: "",
            };
        },

        syncParentDriverInfo(data) {
            this.newFacility.driverType = data.driverType;
            this.newFacility.version = data.version;
            this.newFacility.deviceId = data.deviceId;
        },

        // ===== 데이터 준비 관련 메서드 =====
        prepareFacilityOption() {
            if (typeof this.newFacility.facilityOption !== "string") {
                this.newFacility.facilityOption = JSON.stringify(this.newFacility.facilityOption);
            }
        },

        // ===== 상태 확인 관련 메서드 =====
        hasDriverChanged() {
            return (
                this.newFacility.driverType !== this.orgFacility.driverType ||
                this.newFacility.version !== this.orgFacility.version
            );
        },

        hasFacilityNameChanged() {
            return this.newFacility.facilityName !== this.orgFacility.facilityName;
        },

        // ===== 이벤트 핸들러 =====
        onInputFacilityAlias() {
            this.aliasSynced = false;
            this.$emit("input-facility-alias");
        },

        onSetPulseUnit() {
            this.$emit("set-pulse-unit");
        },

        handleTabClick(index) {
            this.activeTab = index;
        },

        // ===== 유틸리티 메서드 =====
        isEmpty(value) {
            return value === null || value === undefined || value === "";
        },

        // 종합적인 에러 체크 (HTTP 상태 + API 응답)
        checkError(data, status) {
            return this.checkHttpStatus(status) || this.checkApiResponse(data);
        },
        // HTTP 응답 상태 체크
        checkHttpStatus(status) {
            return status !== 200;
        },

        // API 응답 데이터 유효성 체크
        checkApiResponse(data) {
            return !data || data.error || data.status === "error";
        },
    },
};
</script>

<style scoped>
.badge {
    font-size: 12px;
    font-weight: 500;
    margin-right: 4px;
    display: inline-block;
    min-width: 20px;
    padding: 0px 10px 0px 10px;
    color: #fff;
    text-align: center;
    vertical-align: middle;
    white-space: nowrap;
    line-height: 22px;
    border-radius: 8px;
}

.badge.title {
    background-color: #ff6d00;
    color: #fff;
}

.badge.id {
    background-color: #546e7a;
    color: #fff;
}

.sub-facility-title {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    font-size: 14px;
    font-weight: 600;
    gap: 9px;
    margin-bottom: 10px;
}
.sub-facility-wrapper {
    height: 100%;
}

.sub-facility-tab-content {
    padding: 20px;
    border-top: 1px solid #f1f3f4;
}

.sub-facility-tab-pane {
    width: 100%;
    height: 100%;
}

.debug-info {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 10px;
    margin-bottom: 15px;
    font-size: 12px;
}

.debug-info p {
    margin: 5px 0;
}

/* tabs */
.custom-tabs {
    display: flex;
    margin: 0;
    padding: 0;
    list-style: none;
    background: #fff;
}

.custom-tab {
    padding: 0.5rem 1rem;
    cursor: pointer;
    font-size: 0.8rem;
    font-weight: 600;
    background: transparent;
    color: #6c757d;
    border: none;
    border-bottom: 2px solid transparent;
    transition: all 0.2s ease;
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    min-width: 120px;
    justify-content: center;
}

.custom-tab:hover {
    color: #495057;
    background: #f8f9fa;
    font-weight: 600;
}

.custom-tab.active {
    font-weight: 600;
    border-bottom-color: #007bff;
}

.sub-facility-content-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.sub-facility-content {
    width: 70%;
    border: 1px solid #f1f3f4;
    padding: 25px;
}
</style>
